/**
 * One online battle of the game client (design D5, client side): the room's 'just' battle played in lockstep with the
 * other participants through the backend's LockstepCoordinator (src/session/lockstep.ts).
 *
 *  - Start: the battle is built from the server's OnlineBattleInit and seed in the NEUTRAL battle context
 *    (startOnlineBattle, src/game/runtime/online-battle.ts) with a LockstepInput: during the local team's turn the
 *    live keyboard and interface (LocalInput) drive the battle and every tick's effective input is recorded
 *    (coordinator.recordLocal); every other tick plays the frame the coordinator knows.
 *  - The game loop (FWP._main -> OnlineLobby.frame -> step) runs a battle tick only when its input is known
 *    (LockstepInput.canAdvance), several ticks per frame while it is behind the known input (catch-up, also the
 *    fast-forward of a rebuild or a reload: the frame is drawn once after all of them), and shows "Ход соперника…"
 *    while it waits.
 *  - The interface takes input only in the local turn (LockstepInput.interactive: the weapon sheet, the scanner, the
 *    surrender button and map clicks are inert otherwise); input the interface made between frames while the turn
 *    stopped being local is dropped (BattleInput.discardInterfaceInput). Unlike a solo battle, the weapon sheet and the
 *    scanner do not pause an online battle (LockstepInput.freezesMenus): the turn clock keeps running while the player
 *    chooses (a choice is checked when its tick starts, as the server's replay checks it), and they close by themselves
 *    when the turn stops being local.
 *  - Turn ends, forced turns and their reports, per-turn checkpoints (session.hash) and the check of the turn events
 *    against the local simulation are done by LockstepInput and the coordinator ('mismatch' / 'desync' events).
 *  - A 'rebuild' (the local simulation used input the server dropped, e.g. input recorded after a forced turn's
 *    closed input) gives the running battle up (abandonOnlineBattle) and starts it again from the server's log with a
 *    fresh coordinator (session.get): the known ticks run as a fast-forward. A desync or a mismatch ends the battle
 *    for this client, but the client does not give up: it disputes the battle (session.dispute) and the server decides
 *    by its own simulation who went out of sync (SessionOutcome.diverged: they lose); the player sees the decision.
 *  - The surrender button works in every turn (a player is never locked into a battle): session.forfeit.
 *  - After a reconnect of the transport (the client resumes its events itself) or an event gap the coordinator is
 *    brought up to date from the server's log (session.get + resync).
 *  - The end: end_game_fn gives the battle's result (winner, final hash); the client sends session.finish once its
 *    input is delivered and shows the result with the server's verdict (session.ended). No rewards: the original's
 *    'just' mode gives none.
 * The match never blocks the game (no NET_INIT): requests run in the background and their results are applied in the
 * next frame (tasks).
 *
 * Browser-safe: no node imports; the protocol shapes are read structurally (the game does not import src/shared).
 */
import type { LockstepCoordinator } from '../../session/lockstep';
import type { OnlineBattleInit } from '../../session/online';
import { LocalInput, type BattleRunResult } from './battle-input';
import { LockstepInput } from './lockstep-input';
import { abandonOnlineBattle, startOnlineBattle, type NeutralBattleContext } from './online-battle';
import { MSG_OPPONENT_SILENT, MSG_OPPONENT_TURN, MSG_OWN_TEAM, MSG_RESTORING } from './online-texts';
import { playerName } from './i18n';
import { sessionEnded } from './net-policy';

/** The server's outcome of a session (SessionOutcome of src/shared/protocol/model.ts). */
export interface OnlineOutcome {
  result: { winnerTeam: number };
  verified: boolean;
  verdict: 'verified' | 'rejected' | 'unverified';
  reason: string | null;
  consensus: boolean;
  replayId: string | null;
  /** A battle ended by a dispute: the participants the server's simulation contradicted. */
  diverged?: string[];
}

/** The session as the game reads it (SessionInfo of src/shared/protocol/model.ts). */
export interface OnlineSession {
  sessionId: string;
  roomId: string | null;
  seed: number;
  turnTimeSec: number;
  online: OnlineBattleInit | null;
  participants: { playerId: string; displayName: string; teams: number[]; connected: boolean; finished: boolean; forfeited: boolean }[];
  state: 'active' | 'ended';
  turn: number;
  outcome: OnlineOutcome | null;
}

/** Why the battle ended for this client. */
export type MatchEnd = 'finished' | 'forfeited' | 'opponent_left' | 'desync' | 'lost';

/** Batches of recorded input are sent at least every N ticks (100 ms at 50 ticks per second). */
export const ONLINE_FLUSH_EVERY_TICKS = 5;
/** Ticks the known input may run ahead of the battle before the loop catches up with several ticks per frame. */
export const CATCH_UP_LAG = 10;
/** At most this many battle ticks per frame, and at most this much wall-clock time. */
export const MAX_TICKS_PER_FRAME = 400;
export const FRAME_BUDGET_MS = 14;
/** The waiting notice appears after this long without a tick (short gaps between input batches are normal). */
export const STALL_NOTICE_MS = 400;

type Phase = 'starting' | 'playing' | 'rebuilding' | 'ended' | 'closed';

export interface MatchStats {
  rebuilds: number;
  resyncs: number;
  /** Most battle ticks run in one frame. */
  maxTicksPerFrame: number;
  /** Frames in which the battle waited for input. */
  stalledFrames: number;
  /** Times interface input made outside the local turn was dropped. */
  droppedInput: number;
}

export class OnlineMatch {
  readonly sessionId: string;
  readonly init: OnlineBattleInit;
  readonly seed: number;
  readonly turnTimeSec: number;
  /** Teams this client controls. */
  readonly localTeams: number[];
  coordinator: LockstepCoordinator;
  input: LockstepInput | null = null;
  phase: Phase = 'starting';
  /** The local simulation's result (end_game_fn). */
  result: BattleRunResult | null = null;
  /** The server's outcome (session.ended, session.finish, session.get). */
  outcome: OnlineOutcome | null = null;
  end: MatchEnd | null = null;
  finishStatus: 'none' | 'sending' | 'sent' | 'failed' = 'none';
  readonly stats: MatchStats = { rebuilds: 0, resyncs: 0, maxTicksPerFrame: 0, stalledFrames: 0, droppedInput: 0 };
  /** Diagnostics (tests, logs). */
  readonly trace: string[] = [];
  /** Increases whenever the result to show changes (the result screen re-renders). */
  version = 0;

  private handle: NeutralBattleContext | null = null;
  private readonly tasks: (() => void)[] = [];
  private offs: (() => void)[] = [];
  private coordinatorOffs: (() => void)[] = [];
  private status: string = 'open';
  private stallSince: number | null = null;
  private shownInteractive: boolean | null = null;
  private haltRetries = 0;
  private pendingDesyncs: { tick: number; hashes: Record<string, string> }[] = [];

  constructor(
    readonly fwp: any,
    readonly session: OnlineSession,
    coordinator: LockstepCoordinator,
    /** The player's own id (participants). */
    readonly playerId: string,
  ) {
    if (!session.online) throw new Error('OnlineMatch: the session is not an online battle');
    this.sessionId = session.sessionId;
    this.init = session.online;
    this.seed = session.seed;
    this.turnTimeSec = session.turnTimeSec || session.online.turnTimeSec;
    this.localTeams = session.participants.find((p) => p.playerId === playerId)?.teams.slice() ?? [];
    this.coordinator = coordinator;
    this.attachCoordinator(coordinator);
    const services = fwp.services;
    if (typeof services.on === 'function') {
      this.offs.push(
        services.on('session.ended', (e: any) => {
          if (e && e.sessionId === this.sessionId) this.queue(() => this.onOutcome(e.outcome));
        }),
      );
    }
    if (typeof services.connectionStatus === 'string') this.status = services.connectionStatus;
    if (typeof services.onConnectionStatus === 'function') {
      this.offs.push(
        services.onConnectionStatus((s: string) => {
          const was = this.status;
          this.status = s;
          // Back after a reconnect: the client resumed its events; the log catches up with the server's.
          if (s === 'open' && was !== 'open') this.queue(() => this.resync('reconnected'));
        }),
      );
    }
    if (typeof services.onEventGap === 'function') this.offs.push(services.onEventGap(() => this.queue(() => this.resync('event gap'))));
  }

  get services(): any {
    return this.fwp.services;
  }

  private log(line: string): void {
    this.trace.push(line);
    if (this.trace.length > 200) this.trace.splice(0, this.trace.length - 200);
  }

  /** Runs `fn` in the next frame of the game loop (results of background requests, events). */
  private queue(fn: () => void): void {
    this.tasks.push(fn);
  }

  private attachCoordinator(c: LockstepCoordinator): void {
    for (const off of this.coordinatorOffs) off();
    // Events of a coordinator this match no longer plays with (a rebuild replaced it) are ignored.
    const live = () => this.coordinator === c && this.phase !== 'rebuilding';
    this.coordinatorOffs = [
      // Synchronous: the coordinator must stop at once (its local move starts belong to the abandoned simulation).
      c.on('rebuild', (r) => {
        if (live()) this.beginRebuild(`from tick ${r.fromTick}: ${r.reason}`);
      }),
      c.on('mismatch', (m) => {
        if (live()) this.queue(() => live() && this.fail('desync', `mismatch in tick ${m.tick}: ${m.reason}`, m.tick));
      }),
      c.on('desync', (d) => this.queue(() => this.pendingDesyncs.push(d))),
      c.on('halted', (err) => {
        if (live()) this.queue(() => live() && this.onHalted(err));
      }),
      c.on('gap', (g) => this.log(`gap: ${g.reason}`)),
      c.on('error', (err) => this.log(`error: ${err.message}`)),
    ];
  }

  /**
   * The server saw different state hashes for a tick (session.desync). This client's own report may be the stale one
   * of a simulation it has abandoned since (a rebuild after dropped input): the check waits until the current
   * simulation has hashed that tick and ends the battle only when its hash still differs from another participant's.
   */
  private checkDesyncs(): void {
    if (this.phase !== 'playing' && this.phase !== 'starting') return;
    const keep: { tick: number; hashes: Record<string, string> }[] = [];
    for (const d of this.pendingDesyncs) {
      const mine = this.coordinator.localHashes.get(d.tick);
      const others = Object.entries(d.hashes).filter(([pid]) => pid !== this.playerId).map(([, h]) => h);
      if (mine === undefined) {
        // Not simulated that far (yet): checked once the battle got there.
        keep.push(d);
        continue;
      }
      if (others.every((h) => h === mine)) {
        this.log(`desync in tick ${d.tick}: the current simulation agrees (a report of an abandoned one)`);
        continue;
      }
      this.pendingDesyncs = [];
      this.fail('desync', `desync in tick ${d.tick}`, d.tick);
      return;
    }
    this.pendingDesyncs = keep;
  }

  /** Starts the battle (the map loads first). */
  launch(): void {
    const f = this.fwp;
    const input = new LockstepInput(this.coordinator, new LocalInput(f), { checkpoints: true });
    this.input = input;
    this.shownInteractive = null;
    this.phase = 'starting';
    this.handle = startOnlineBattle(f, {
      init: this.init,
      seed: this.seed,
      input,
      checkpoints: true,
      onEnd: (r) => this.onBattleEnd(input, r),
    });
  }

  /** The running battle is this match's (its seam takes this match's input). */
  owns(proc: any): boolean {
    return !!proc && !!this.input && !!proc.battle_input && proc.battle_input.source === this.input;
  }

  /** One frame of the game loop; true when it ran the battle (FWP._main must not run proc.ongoing then). */
  frame(): boolean {
    const f = this.fwp;
    const now = Number(f.net_now());
    for (let n = this.tasks.length; n > 0 && this.tasks.length > 0; n--) this.tasks.shift()!();
    if (this.pendingDesyncs.length) this.checkDesyncs();
    const mine = f.main_mode == 'LOCAL_GAME' && this.owns(f._proc);
    if (this.phase === 'starting' && mine) this.phase = 'playing';
    if (this.phase === 'rebuilding') {
      if (mine) f.net_notice(MSG_RESTORING, 300);
      return mine; // the abandoned simulation does not run while the new log is fetched
    }
    if (this.phase !== 'playing' || !mine) return false;
    this.step(now);
    return true;
  }

  private step(now: number): void {
    const f = this.fwp;
    const p = f._proc;
    const input = this.input!;
    const c = this.coordinator;
    const interactive = input.interactive;
    if (!interactive) {
      // A turn that stopped being local between two frames: the interface's pending input is not the battle's, and the
      // weapon sheet or the scanner the player kept open closes.
      this.closeMenus(p);
      if (p.battle_input && p.battle_input.discardInterfaceInput(p)) this.stats.droppedInput++;
    }
    if (interactive !== this.shownInteractive) {
      // The game menu shows the weapon sheet, scanner and surrender buttons only in the local turn.
      this.shownInteractive = interactive;
      if (f._interf && typeof f._interf.render_game_menu === 'function') f._interf.render_game_menu();
    }
    let ran = 0;
    let stalled = false;
    for (;;) {
      const next = Number(p.battle_tick) + 1;
      if (!input.canAdvance(next)) {
        stalled = ran === 0;
        break;
      }
      const before = Number(p.battle_tick);
      p.ongoing();
      if (f._proc !== p || this.phase !== 'playing') break;
      if (Number(p.battle_tick) === before) break; // no tick ran (a move start retried, the battle not set up yet)
      ran++;
      // Not behind the known input: one tick per frame, as the game runs offline.
      if (!c.canAdvance(Number(p.battle_tick) + 1 + CATCH_UP_LAG)) break;
      if (ran >= MAX_TICKS_PER_FRAME || Number(f.net_now()) - now >= FRAME_BUDGET_MS) break;
    }
    if (ran > this.stats.maxTicksPerFrame) this.stats.maxTicksPerFrame = ran;
    if (ran > 20) f.net_notice(MSG_RESTORING, 300);
    if (stalled) {
      this.stats.stalledFrames++;
      if (this.stallSince === null) this.stallSince = now;
      if (now - this.stallSince >= STALL_NOTICE_MS) f.net_notice(c.isInputClosed() ? MSG_OPPONENT_SILENT : MSG_OPPONENT_TURN, 300);
    } else this.stallSince = null;
    // The whole of the opponent's turn says whose it is (the touch controls are hidden then), not only a stall.
    if (!stalled && ran <= 20 && this.phase === 'playing' && f._proc === p && !c.isLocalTurn()) f.net_notice(MSG_OPPONENT_TURN, 300);
  }

  /** Closes the weapon sheet (without a choice) or the scanner the local player keeps open. */
  private closeMenus(p: any): void {
    const f = this.fwp;
    if (p.wpsheet) {
      p.close_wpsheet(0);
      return;
    }
    const i = f._interf;
    const top = i && i.cur_screen && i.cur_screen[0] ? i.cur_screen[i.cur_screen[0]] : null;
    if (top && top.name == 'scaner') {
      i.remove();
      p.GAME_FREEZE = false;
      f.stage_focus();
    }
  }

  /** The waiting state for the interface and tests: the battle waits for the input of another participant. */
  get waiting(): boolean {
    return this.stallSince !== null;
  }

  // ------------------------------------------------------------------------------------------------ end of the battle

  /** end_game_fn of the battle (inside the game's teardown): the local simulation's result. */
  private onBattleEnd(input: LockstepInput, r: BattleRunResult): void {
    if (input !== this.input) return;
    this.result = r;
    this.version++;
    if (this.phase !== 'playing' && this.phase !== 'starting') return;
    this.phase = 'ended';
    this.end = 'finished';
    this.log(`battle over: winner ${r.outcome.winner} in tick ${r.ticks}`);
    this.queue(() => void this.sendFinish());
  }

  /** session.finish with the battle's result and final hash, once every local input and turn end is delivered. */
  private async sendFinish(): Promise<void> {
    const r = this.result;
    if (!r || this.finishStatus !== 'none') return;
    this.finishStatus = 'sending';
    const c = this.coordinator;
    // idle() first: it commits the ticks recorded since the last batch (empty ticks too, which hasUnsent does not see),
    // so the server's log reaches the battle's last tick before the finish report.
    await c.idle();
    for (let i = 0; i < 300 && c.hasUnsent() && !c.failure; i++) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      await c.idle();
    }
    for (let attempt = 0; ; attempt++) {
      try {
        const res = await this.services.finishBattle(this.sessionId, { winnerTeam: Number(r.outcome.winner) }, { finalHash: r.finalHash });
        this.queue(() => {
          this.finishStatus = 'sent';
          this.log(`finish: ${res.status ?? (res.ended ? 'ended' : 'waiting')}`);
          if (res.outcome) this.onOutcome(res.outcome);
          else this.pollOutcome(0);
        });
        return;
      } catch (err: any) {
        const code = err && err.code;
        if (code === 'UNAUTHORIZED' && typeof this.fwp.net_accounts === 'function' && this.fwp.net_accounts()) {
          this.queue(() => void this.endedSession(err));
          return;
        }
        if (code === 'INVALID_STATE') {
          // Already reported (a retry whose answer was lost) or the session ended meanwhile.
          this.queue(() => {
            this.finishStatus = 'sent';
            this.pollOutcome(0);
          });
          return;
        }
        if (code === 'NOT_FOUND' || code === 'FORBIDDEN' || attempt >= 5) {
          this.queue(() => {
            this.finishStatus = 'failed';
            this.log(`finish failed: ${code ?? err}`);
            this.version++;
          });
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }
  }

  /** The verdict comes as session.ended; if that event was missed, the session tells it (every few seconds). */
  private pollOutcome(attempt: number): void {
    if (this.outcome || this.phase === 'closed' || attempt > 60) return;
    setTimeout(() => {
      if (this.outcome || this.phase === 'closed') return;
      this.services.getSession(this.sessionId).then(
        (r: any) => this.queue(() => (r.session.state === 'ended' && r.session.outcome ? this.onOutcome(r.session.outcome) : this.pollOutcome(attempt + 1))),
        () => this.queue(() => this.pollOutcome(attempt + 1)),
      );
    }, 3000);
  }

  /** The server ended the session (the verdict of a finished battle, or a battle over before the simulation's end). */
  private onOutcome(outcome: OnlineOutcome | null): void {
    if (!outcome || this.phase === 'closed') return;
    this.outcome = outcome;
    this.version++;
    this.log(`outcome: winner ${outcome.result.winnerTeam}, ${outcome.verdict}${outcome.reason ? ' (' + outcome.reason + ')' : ''}`);
    if (this.phase === 'ended') return;
    // The battle ended on the server while it runs here: the other participants left it, a dispute was decided (or it
    // was abandoned).
    this.abandonBattle();
    this.phase = 'ended';
    this.end = outcome.diverged ? 'desync' : /forfeit/.test(outcome.reason ?? '') ? 'opponent_left' : 'lost';
  }

  /** The local player leaves the battle (the surrender button): session.forfeit, the battle ends here. */
  forfeit(): void {
    if (this.phase !== 'playing' && this.phase !== 'starting' && this.phase !== 'rebuilding') return;
    this.log('forfeit');
    this.abandonBattle();
    this.phase = 'ended';
    this.end = 'forfeited';
    this.version++;
    this.sendForfeit();
  }

  private sendForfeit(): void {
    this.services.forfeitBattle(this.sessionId).then(
      () => this.queue(() => this.pollOutcome(0)),
      (err: any) => this.queue(() => {
        this.log(`forfeit failed: ${err?.code ?? err}`);
        if (this.endedSession(err)) return;
        this.pollOutcome(0);
      }),
    );
  }

  /**
   * The battle cannot go on for this client (the simulations diverged, the session is lost): it ends here. A divergence
   * (`tick`) is disputed (session.dispute): the server decides who went out of sync and ends the session for everyone,
   * so nobody is kept waiting and nobody wins by making this client give up.
   */
  private fail(end: MatchEnd, why: string, tick?: number): void {
    if (this.phase === 'ended' || this.phase === 'closed') return;
    this.log(`fail (${end}): ${why}`);
    this.abandonBattle();
    this.phase = 'ended';
    this.end = end;
    this.version++;
    if (end === 'desync') this.sendDispute(tick ?? 0, why);
  }

  private sendDispute(tick: number, why: string): void {
    const services = this.services;
    if (typeof services.disputeBattle !== 'function') return this.pollOutcome(0);
    services.disputeBattle(this.sessionId, tick, why).then(
      (res: any) =>
        this.queue(() => {
          this.log(`dispute: ${res?.status ?? '?'}`);
          if (res && res.outcome) this.onOutcome(res.outcome);
          else this.pollOutcome(0);
        }),
      (err: any) =>
        this.queue(() => {
          this.log(`dispute failed: ${err?.code ?? err}`);
          if (this.endedSession(err)) return;
          this.pollOutcome(0);
        }),
    );
  }

  /** The coordinator stopped delivering local input: see whether the session is still running. */
  private onHalted(err: Error): void {
    this.log(`halted: ${err.message}`);
    if (this.endedSession(err)) return;
    const c = this.coordinator;
    this.services.getSession(this.sessionId).then(
      (r: any) =>
        this.queue(() => {
          if (r.session.state === 'ended') return this.onOutcome(r.session.outcome);
          if (this.coordinator !== c || this.phase !== 'playing') return;
          if (this.haltRetries++ < 3) setTimeout(() => this.queue(() => void c.resume()), 2000);
          else this.fail('lost', `delivery halted: ${err.message}`);
        }),
      (e: any) => this.queue(() => (this.endedSession(e) ? undefined : e?.code === 'NOT_FOUND' ? this.fail('lost', 'the session is gone') : undefined)),
    );
  }

  /**
   * A request of the battle was refused as UNAUTHORIZED: the player's session is over (the backend services renew it
   * when they can). With the game's accounts the battle is given up and the game restarts into the sign-in screen
   * (net-policy.ts sessionEnded); true when that happened.
   */
  private endedSession(err: any): boolean {
    if (!err || err.code !== 'UNAUTHORIZED' || this.phase === 'closed') return false;
    if (!sessionEnded(this.fwp, err)) return false;
    this.abandonBattle();
    this.phase = 'ended';
    this.version++;
    return true;
  }

  /** Gives the running (or loading) battle up without a result. */
  private abandonBattle(): void {
    if (this.handle && this.handle.active) abandonOnlineBattle(this.fwp, this.handle);
    this.handle = null;
  }

  // ------------------------------------------------------------------------------------------------ recovery

  /** The coordinator's log catches up with the server's (after a reconnect or lost events). */
  private resync(why: string): void {
    if (this.phase !== 'playing' && this.phase !== 'starting') return;
    this.stats.resyncs++;
    this.log(`resync: ${why}`);
    const c = this.coordinator;
    this.services.getSession(this.sessionId).then(
      (r: any) =>
        this.queue(() => {
          if (r.session.state === 'ended') return this.onOutcome(r.session.outcome);
          if (this.coordinator === c && (this.phase === 'playing' || this.phase === 'starting')) c.resync(r.log);
        }),
      (err: any) => {
        this.log(`resync failed: ${err?.code ?? err}`);
        this.queue(() => void this.endedSession(err));
      },
    );
  }

  /**
   * The local simulation used input the server does not have: the coordinator stops now (its move starts belong to
   * the abandoned simulation), the server's log comes in a fresh coordinator and the battle starts again from it.
   */
  private beginRebuild(why: string): void {
    if (this.phase !== 'playing' && this.phase !== 'starting') return;
    this.phase = 'rebuilding';
    this.stats.rebuilds++;
    this.log(`rebuild: ${why}`);
    this.coordinator.dispose();
    this.fetchCoordinator(0);
  }

  private fetchCoordinator(attempt: number): void {
    this.services.resumeLockstep(this.sessionId, { flushEveryTicks: ONLINE_FLUSH_EVERY_TICKS }).then(
      (c: LockstepCoordinator) => this.queue(() => this.restartWith(c)),
      (err: any) =>
        this.queue(() => {
          if (this.phase !== 'rebuilding') return;
          if (this.endedSession(err)) return;
          if (err?.code === 'NOT_FOUND' || err?.code === 'FORBIDDEN' || attempt >= 10) return this.fail('lost', `rebuild failed: ${err?.code ?? err}`);
          setTimeout(() => this.queue(() => this.fetchCoordinator(attempt + 1)), 1000);
        }),
    );
  }

  private restartWith(c: LockstepCoordinator): void {
    if (this.phase !== 'rebuilding') {
      c.dispose();
      return;
    }
    this.abandonBattle();
    this.coordinator = c;
    this.attachCoordinator(c);
    this.launch();
    this.log(`rebuilt from the log (turn ${c.turn}, through tick ${c.log.current.throughTick})`);
  }

  /** The match is over for the interface: every subscription and the coordinator go. */
  close(): void {
    if (this.phase === 'closed') return;
    this.abandonBattle();
    this.phase = 'closed';
    for (const off of [...this.offs, ...this.coordinatorOffs]) off();
    this.offs = [];
    this.coordinatorOffs = [];
    this.coordinator.dispose();
  }

  /** Winner of the battle as far as it is known: the server's outcome, else the local simulation's result. */
  get winner(): number | null {
    if (this.outcome) return Number(this.outcome.result.winnerTeam);
    if (this.result) return Number(this.result.outcome.winner);
    return null;
  }

  /**
   * A team's name in the battle (its HUD bar and the log's turn lines: FWP.team_label): its name with the player who
   * plays it, the local player's own team marked instead. Display only: the battle's teams keep the init's names.
   */
  battleTeamLabel(team: number, name: string): string {
    if (this.localTeams.includes(team)) return name + ' ' + MSG_OWN_TEAM;
    const who = this.session.participants.find((p) => p.teams.includes(team));
    return who ? name + ' (' + playerName(who.displayName) + ')' : name;
  }

  /** Name of a team for the result screen: its name and the player who played it. */
  teamLabel(team: number): string {
    const t = this.init.teams[team - 1];
    if (!t) return String(team);
    const who = this.session.participants.find((p) => p.teams.includes(team));
    return who ? t.name + ' (' + who.displayName + ')' : t.name;
  }
}
